import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const DeliveryStatusBadge = ({ status }) => {
  const config = {
    sent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Sent' },
    delivered: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Delivered' },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
    bounced: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Bounced' },
  };
  const c = config[status] || config.pending;
  return <Badge className={`${c.bg} ${c.text} border-0`}>{c.label}</Badge>;
};

const WorkflowDeliveryLogsPanel = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/platform/system-automations/delivery-logs?limit=50`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="workflow-delivery-logs-panel">
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 border-zinc-700 bg-zinc-800 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="border-zinc-700 bg-zinc-800">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchLogs} className="border-zinc-700 text-zinc-300">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {logs.length === 0 ? (
        <Card className="border-zinc-800 bg-zinc-900/50">
          <CardContent className="py-12 text-center">
            <Activity className="mx-auto mb-4 h-12 w-12 text-zinc-600" />
            <p className="text-zinc-400">No delivery logs yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800">
          <Table>
            <TableHeader className="bg-zinc-900/80">
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Timestamp</TableHead>
                <TableHead className="text-zinc-400">Recipient</TableHead>
                <TableHead className="text-zinc-400">Type</TableHead>
                <TableHead className="text-zinc-400">Provider</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400">Message ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell className="text-sm text-zinc-400">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="text-white">{log.recipient_email}</TableCell>
                  <TableCell className="text-sm text-zinc-300">{log.email_type?.replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    <span className="rounded bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                      {log.provider_used?.replace(/_/g, ' ')}
                    </span>
                  </TableCell>
                  <TableCell><DeliveryStatusBadge status={log.status} /></TableCell>
                  <TableCell>
                    {log.mailgun_message_id ? (
                      <code className="block max-w-[150px] truncate text-xs text-zinc-500">
                        {log.mailgun_message_id}
                      </code>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default WorkflowDeliveryLogsPanel;
